import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { Grid, Button, Box, Typography } from "@material-ui/core";
import { DropzoneAreaBase, FileObject } from "material-ui-dropzone";
import { Alert } from "@material-ui/lab";
import { CURRENT_API_VERSION } from "../../../../../api/apiConfig";
import CompanyHeading from "../../../../theming/CompanyHeading";
import useCsvUserAddValidation from "../../../../hooks/useCsvUserAddValidation";
import BulkMembershipOverviewList from "./BulkMembershipOverviewList";
import apiRoutes from "../../../../../api/apiRoutes";
import { useSelector } from "react-redux";
import { AppState } from "../../../../../redux";

interface CsvBulkUploadProps {
  organizationId: string;
  addUserWithoutMail: boolean;
}

// TODO put in separate file
export type Stateful<T> = {
  value: T;
  setValue: React.Dispatch<React.SetStateAction<T>>;
};

export function useStateful<T>(initialValue: T): Stateful<T> {
  const [value, setValue] = React.useState<T>(initialValue);
  return { value, setValue };
}

/**
 * Component for bulk uploading membership creation via CSV file.
 */
const CsvBulkUpload: React.FC<CsvBulkUploadProps> = ({
  organizationId,
  addUserWithoutMail,
}: CsvBulkUploadProps) => {
  const { t } = useTranslation(["common", "organization"]);
  const [csvFiles, setCsvFiles] = useState<FileObject[]>([]);

  const downloadUrl = `${CURRENT_API_VERSION}${apiRoutes.download_template}`;

  const addressRequired = useSelector(
    (state: AppState) => state.metaData.metaData.address_required
  );

  const [showUploadSection, setShowUploadSection] = useState(true);
  const bulkAddFinished = useStateful(false);
  const bulkAddedCount = useStateful(0);
  const bulkBookingLoading = useStateful(false);

  const { validateCsv, validUserData, invalidUserData, setValidUserData } =
    useCsvUserAddValidation(addUserWithoutMail);

  useEffect(() => {
    // Clear uploaded CSV files if there are invalid data
    if (invalidUserData?.length > 0) {
      setCsvFiles([]);
      setShowUploadSection(true);
    }
  }, [invalidUserData]);

  /**
   * Handler function for adding CSV files.
   * @param {FileObject[]} addedCsv - The CSV files to be added.
   */
  const handleAddCSV = async (addedCsv: FileObject[]) => {
    addedCsv.forEach((file) => {
      setCsvFiles((prev) => [...prev, file]);
    });
  };

  /**
   * Handler function for validating the uploaded CSV files.
   */
  const handleValidateCSV = () => {
    setShowUploadSection(false);
    setValidUserData([]);
    validateCsv(csvFiles, organizationId);
  };

  return (
    <>
      <CompanyHeading>
        {t("membership.addMembers", { ns: "organization" })}
      </CompanyHeading>
      {invalidUserData?.length > 0 && (
        <Alert severity="error" style={{ marginTop: "2rem", marginBottom: "2rem" }}>
          <Typography>
            <b>{t("csv.csvInvalidFormat", { ns: "organization" })}</b>
          </Typography>
          <Typography>
            {t("csv.csvInvalidFormatText", { ns: "organization" })}
          </Typography>
          <br />
          {invalidUserData.map((userData, index) => (
            <Typography key={index}>{userData}</Typography>
          ))}
        </Alert>
      )}

      <Box style={{ marginTop: "2rem", marginBottom: "2rem" }}>
        {validUserData?.length > 0 && invalidUserData.length === 0 && (
          <BulkMembershipOverviewList
            membersToAdd={validUserData}
            setValidMembersToAdd={setValidUserData}
            setShowUploadSection={setShowUploadSection}
            bulkAddFinished={bulkAddFinished}
            bulkAddedCount={bulkAddedCount}
            bulkBookingLoading={bulkBookingLoading}
          />
        )}
      </Box>
      {showUploadSection ? (
        <>
          <DropzoneAreaBase
            filesLimit={1}
            showAlerts={false}
            fileObjects={csvFiles}
            onAdd={handleAddCSV}
            onDelete={(deletedFile) =>
              setCsvFiles((prev) => prev.filter((f) => f !== deletedFile))
            }
            acceptedFiles={[".csv"]}
            maxFileSize={2 * 1024 * 1024}
            dropzoneText={t("csv.uploadCsvNote", { ns: "organization" })}
            onDropRejected={() => {
              enqueueSnackbar(t("csvUploadFail", { ns: "snackbars" }), {
                variant: "error",
              });
            }}
          />
          <Box style={{ paddingTop: ".5rem", paddingBottom: "1rem" }}>
            <Typography variant="caption">
              {t("csv.onlyOneFileAtaTime", { ns: "organization" })} <br />
              {t(
                addressRequired
                  ? "csv.uploadFormatTextAddressRequired"
                  : "csv.uploadFormatTextBasic",
                { ns: "organization" }
              )}{" "}
              <br />
              {t("csv.donwloadUploadHint", { ns: "organization" })}
            </Typography>
          </Box>

          <Box style={{ marginTop: 20 }}>
            <Grid
              container
              spacing={2}
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Grid item xs={12} sm={6} md={4}>
                <Button
                  id="add-organization"
                  type="submit"
                  size="large"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={csvFiles.length === 0}
                  onClick={handleValidateCSV}
                >
                  {t("csv.uploadCsvButton", { ns: "organization" })}
                </Button>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <a href={downloadUrl}>
                  <Button
                    id="add-organization"
                    type="submit"
                    size="large"
                    variant="outlined"
                    color="primary"
                    fullWidth
                  >
                    {t("csv.downloadCsvTemplate", { ns: "organization" })}
                  </Button>
                </a>
              </Grid>
            </Grid>
          </Box>
        </>
      ) : null}
    </>
  );
};

export default CsvBulkUpload;
